import useWindowSize from "@shared/hooks/useWindowSize";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import { Timer } from "@ui-kit/feedback/timer";
import Button from "@ui-kit/form/Button/Button.web";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useMemo } from "react";
import CartTimerCheckout from "./CartTimerCheckout.web";
import { useAllowAddMoreCartTime } from "./hooks/useAllowAddMoreCartTime";

export default function CartTimer() {
  return <CartTimer.Banner />;
}

/**
 * Displays the timer as plain text in "Time left M:SS" style.
 * Red text on white/minimal background. Uses useBasketService for expiry and time extension.
 */
CartTimer.Text = function CartTimerText() {
  const { t } = useTranslation("common");
  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => windowWidth && windowWidth < breakpoints.sm - 1,
    [windowWidth]
  );

  const { expiryDate, hasItems, paymentVenue, isBasketUpdating, extendBasket } =
    useBasketService<IBasket>();
  const allowAddMoreTime = useAllowAddMoreCartTime(expiryDate, paymentVenue);
  const targetDate = expiryDate;

  if (!expiryDate || !hasItems) {
    return null;
  }

  const completionText = t("left to complete transaction!");

  return (
    <Flex
      data-testid="cart-timer"
      w="full"
      color="red.600"
      flex="0 0 100%"
      alignItems="center"
      px={0}
      h="auto"
      flexDirection={{ base: "column", xs: "row" }}
      justifyContent="flex-start"
      gap={{ base: "2xs", sm: "sm" }}
      direction={{ base: "row", sm: "row" }}
    >
      <Text fontSize="sm" fontWeight="600" color="red.600">
        {t("Time left")}{" "}
        <Timer
          targetDate={targetDate as Date}
          announcementSuffix={completionText}
        />
      </Text>

      {allowAddMoreTime && (
        <Box flexShrink={0} lineHeight="1">
          <Button
            data-testid="cart-timer-button"
            size={isSmallerThanSm ? "xs" : "sm"}
            colorPalette="red"
            variant="outline"
            onClick={extendBasket}
            isLoading={isBasketUpdating}
          >
            <Text
              as="span"
              display={{
                base: "none",
                xs: "inline",
              }}
            >
              {t("I need more time")}
            </Text>
            <Text
              as="span"
              display={{
                base: "inline",
                xs: "none",
              }}
            >
              {t("Need more time")}
            </Text>
          </Button>
        </Box>
      )}
    </Flex>
  );
};

/**
 * Displays the timer as a banner with a light background.
 * Uses useBasketService to manage basket expiry and time extension functionality.
 * Includes the Add More Time button.
 */
CartTimer.Banner = function CartTimerBanner() {
  const { t } = useTranslation("common");
  const { width: windowWidth } = useWindowSize();
  const isSmallerThanSm = useMemo(
    () => windowWidth && windowWidth < breakpoints.sm - 1,
    [windowWidth]
  );

  const { expiryDate, hasItems, paymentVenue, isBasketUpdating, extendBasket } =
    useBasketService<IBasket>();
  const allowAddMoreTime = useAllowAddMoreCartTime(expiryDate, paymentVenue);
  const targetDate = expiryDate;

  const completionText = t("left to complete transaction!");

  if (!expiryDate || !hasItems) {
    return null;
  }

  return (
    <Flex
      data-testid="cart-timer"
      w="full"
      backgroundColor="red.100"
      color="red.900"
      flex="0 0 100%"
      alignItems="center"
      flexDirection="row"
      justifyContent="center"
      px={0}
      h={{ base: "element.3xl", sm: "element.4xl" }}
      gap={{ sm: "lg", xs: "2xs", base: "2xs", md: "lg" }}
      direction={{ base: "row", sm: "column" }}
    >
      <Flex direction="row" alignItems="center" gap="2xs">
        <Box
          display={{
            base: !allowAddMoreTime ? "block" : "none",
            xs: "block",
          }}
        >
          <Icon.Clock outline aria-hidden="true" color="red.900" />
        </Box>
        <Text fontSize="sm" fontWeight="700" textAlign="center">
          {/* Silencing the visual time AND injecting the suffix */}
          <Timer
            targetDate={targetDate as Date}
            announcementSuffix={completionText}
            disableLiveAnnouncements
          />{" "}
          {/* Silencing the visual suffix text */}
          <span aria-hidden="true">{completionText}</span>
        </Text>
      </Flex>

      {allowAddMoreTime && (
        <Box p="2xs" justifyContent="center" lineHeight="1">
          <Button
            data-testid="cart-timer-button"
            size={isSmallerThanSm ? "xs" : "sm"}
            colorPalette="secondary"
            variant="outline"
            onClick={extendBasket}
            isLoading={isBasketUpdating}
          >
            <Text
              as="span"
              display={{
                base: "none",
                xs: "inline",
              }}
            >
              {t("I need more time")}
            </Text>
            <Text
              as="span"
              display={{
                base: "inline",
                xs: "none",
              }}
            >
              {t("Need more time")}
            </Text>
          </Button>
        </Box>
      )}
    </Flex>
  );
};

/**
 * Used on the public checkout.
 * Uses useBasketService to manage basket expiry and time extension functionality.
 * Includes the Add More Time button.
 */
CartTimer.Checkout = CartTimerCheckout;
/**
 * Used on the public seating cart.
 * Uses useBasketService to check basket expiry and item status.
 * Does not include an Add More Time button.
 */
CartTimer.Seating = function CartTimerBannerSeating() {
  const { t } = useTranslation("common");
  const { expiryDate, hasItems } = useBasketService<IBasket>();

  if (!expiryDate || !hasItems) {
    return null;
  }

  return (
    <Flex
      data-testid="cart-timer-seating"
      color="red.900"
      fontSize="xs"
      fontWeight="bold"
      alignItems="center"
      gap="2xs"
    >
      {/* Show "X remaining" text */}
      <Icon.Clock.Outline boxSize="element.sm" aria-hidden="true" />
      <Timer targetDate={expiryDate} disableLiveAnnouncements />
      <Text aria-hidden="true">{t("remaining")}</Text>
    </Flex>
  );
};
